import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Helmet } from "react-helmet-async";
import Heading from "../components/common/Heading";
import SectionDivider from "../components/common/SectionDivider";
import useScrollHighlight from "../hooks/useScrollHighlight";
import { Link } from "react-router-dom";

const galleryData = [
  { id: 1, src: "/assets/img/about-11.jpg", title: "Grand Event Entrance", category: "Weddings & Events", likes: 48 },
  { id: 2, src: "/assets/img/about-12.jpg", title: "Heritage Architecture", category: "Royal Resort", likes: 35 },
  { id: 3, src: "/assets/img/about-13.jpg", title: "Royal Garden View", category: "Lush Lawns", likes: 42 },
  { id: 4, src: "/assets/img/about-14.jpg", title: "Lush Peacock Lawns", category: "Lush Lawns", likes: 39 },
  { id: 5, src: "/assets/img/about-15.jpg", title: "Royal Wedding Setup", category: "Weddings & Events", likes: 56 },
  { id: 6, src: "/assets/img/about-16.jpg", title: "Sunset Courtyard", category: "Royal Resort", likes: 29 },
  { id: 7, src: "/assets/img/about-17.jpg", title: "Floral Stage Decor", category: "Weddings & Events", likes: 51 },
  { id: 8, src: "/assets/img/about-18.jpg", title: "Evening Ambient Lighting", category: "Weddings & Events", likes: 44 },
  { id: 9, src: "/assets/img/carousel-1.webp", title: "Royal Resort Frontage", category: "Royal Resort", likes: 62 },
  { id: 10, src: "/assets/img/carousel-2.jpg", title: "Courtyard Swimming Pool", category: "Royal Resort", likes: 38 },
  { id: 11, src: "/assets/img/Mehrangarh-Fort.jpg", title: "Mehrangarh Fort View", category: "Royal Resort", likes: 52 },
  { id: 12, src: "/assets/img/Umaid-Bhawan-Palace.jpg", title: "Umaid Bhawan Heritage", category: "Royal Resort", likes: 47 },
  { id: 13, src: "/assets/img/Kaylana-Lake.jpg", title: "Kaylana Lake Sunset", category: "Lush Lawns", likes: 34 },
  { id: 14, src: "/assets/img/Ummed-Sagar-Bandh.jpg", title: "Umaid Sagar Lake Serenity", category: "Lush Lawns", likes: 40 },
];

export default function GalleryPage() {
  useScrollHighlight();

  const [activeCategory, setActiveCategory] = useState("All");
  const [activeLayout, setActiveLayout] = useState("masonry"); // "masonry" or "polaroid"
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [likesMap, setLikesMap] = useState({});

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  const filteredItems = galleryData.filter((item) => {
    if (activeCategory === "All") return true;
    return item.category === activeCategory;
  });

  const categories = ["All", "Royal Resort", "Weddings & Events", "Lush Lawns"];

  const handleLike = (id, e) => {
    e.stopPropagation();
    setLikesMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openLightbox = (index) => setLightboxIndex(index);

  return (
    <>
      <Helmet>
        <title>Visual Gallery &amp; Moments – HariKunj Resort Jodhpur</title>
        <meta
          name="description"
          content="Explore the official photo gallery of HariKunj Resort Jodhpur. View heritage architecture, royal wedding setups, lush green peacock lawns, and swimming pool courtyards."
        />
        <link rel="canonical" href="https://harikunjresort.com/gallery" />
      </Helmet>

      {/* Page Header Hero */}
      <Heading subtitle="EXPLORE OUR MOMENTS" title="Photo Gallery" heading="Visual Poetry of HariKunj" />

      <div className="container py-4">
        {/* Top Control Bar: Category Tabs & View Mode Switcher */}
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 mb-4 p-3 rounded-4" style={{ background: "rgba(255,255,255,0.9)", border: "1.5px solid rgba(212,175,55,0.3)", boxShadow: "0 8px 24px rgba(11,29,55,0.06)" }}>
          {/* Category Filter Pills */}
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`btn btn-sm rounded-pill px-3 py-2 fw-semibold ${activeCategory === cat ? "btn-warning text-dark shadow-sm" : "btn-outline-secondary border-0"}`}
                style={{
                  fontSize: "0.84rem",
                  letterSpacing: "0.03em",
                  background: activeCategory === cat ? "linear-gradient(135deg, #FEA116, #D4AF37)" : "transparent",
                  color: activeCategory === cat ? "#050E1A" : "#53647C",
                }}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* View Mode Switcher ("Proper Grid" vs "Wall Frames") */}
          <div className="d-flex align-items-center gap-2 bg-light p-1 rounded-pill border">
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-3 py-1 fw-bold ${activeLayout === "masonry" ? "bg-white text-dark shadow-sm" : "text-muted"}`}
              style={{ fontSize: "0.8rem" }}
              onClick={() => setActiveLayout("masonry")}
            >
              <i className="fas fa-th me-1" /> Grid View
            </button>
            <button
              type="button"
              className={`btn btn-sm rounded-pill px-3 py-1 fw-bold ${activeLayout === "polaroid" ? "bg-white text-dark shadow-sm" : "text-muted"}`}
              style={{ fontSize: "0.8rem" }}
              onClick={() => setActiveLayout("polaroid")}
            >
              <i className="fas fa-images me-1" /> Wall Frames
            </button>
          </div>
        </div>

        {/* PROPER MASONRY GRID LAYOUT */}
        {activeLayout === "masonry" && (
          <div className="row g-3 g-md-4">
            {filteredItems.map((item, idx) => {
              const isLiked = likesMap[item.id];
              const count = item.likes + (isLiked ? 1 : 0);

              return (
                <div key={item.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                  <div
                    className="hk-gallery-card shadow-sm"
                    onClick={() => openLightbox(idx)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openLightbox(idx);
                    }}
                  >
                    <div className="hk-gallery-img-wrapper" onClick={() => openLightbox(idx)}>
                      <img
                        src={item.src}
                        alt={item.title}
                        loading="lazy"
                      />
                      <div className="hk-gallery-overlay">
                        <div
                          className="hk-gallery-zoom-btn"
                          title="View Fullscreen"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLightbox(idx);
                          }}
                        >
                          <i className="fas fa-search-plus" />
                        </div>
                      </div>
                    </div>

                    <div className="hk-gallery-card-body">
                      <div>
                        <h6 className="hk-gallery-card-title">{item.title}</h6>
                        <small className="hk-gallery-card-cat">{item.category}</small>
                      </div>
                      <button
                        type="button"
                        className={`btn btn-sm rounded-pill border ${isLiked ? "btn-danger text-white" : "btn-light text-muted"}`}
                        onClick={(e) => handleLike(item.id, e)}
                        style={{ fontSize: "0.76rem" }}
                      >
                        <i className={`${isLiked ? "fas" : "far"} fa-heart me-1`} /> {count}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CRAZY REALISTIC WALL-HUNG PHOTO FRAMES LAYOUT */}
        {activeLayout === "polaroid" && (
          <div className="row g-4 justify-content-center py-3">
            {filteredItems.map((item, idx) => {
              const isLiked = likesMap[item.id];
              const count = item.likes + (isLiked ? 1 : 0);

              return (
                <div key={item.id} className="col-12 col-sm-6 col-md-4 col-lg-3 position-relative">
                  <div
                    className={`hk-wall-hanging-card hk-frame-${idx % 4}`}
                    onClick={() => openLightbox(idx)}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                  >
                    {/* 3D Brass Push Pin Nail */}
                    <div className="hk-pin-nail" />

                    {/* Real Braided Hanging String SVG */}
                    <svg className="hk-pin-string-svg" viewBox="0 0 120 30" preserveAspectRatio="none">
                      <line x1="60" y1="4" x2="14" y2="30" stroke="#C59B27" strokeWidth="3" strokeLinecap="round" />
                      <line x1="60" y1="4" x2="106" y2="30" stroke="#C59B27" strokeWidth="3" strokeLinecap="round" />
                    </svg>

                    {/* Real Polaroid Photo Frame */}
                    <div className="hk-polaroid-frame">
                      <div className="hk-polaroid-img-box">
                        <img src={item.src} alt={item.title} className="hk-polaroid-img" loading="lazy" />
                      </div>
                      <div className="hk-polaroid-title d-flex align-items-center justify-content-between px-2 mt-2">
                        <span>{item.title}</span>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-danger text-decoration-none"
                          onClick={(e) => handleLike(item.id, e)}
                        >
                          <i className={`${isLiked ? "fas" : "far"} fa-heart`} /> {count}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SectionDivider />

      {/* Booking CTA Strip */}
      <section className="py-5 text-center bg-dark text-white position-relative" style={{ background: "linear-gradient(135deg, #050E1A 0%, #0B1D37 100%)" }}>
        <div className="container">
          <h3 className="font-serif text-warning mb-2" style={{ letterSpacing: "0.08em" }}>Experience HariKunj In Person</h3>
          <p className="text-light-50 mb-4 mx-auto" style={{ maxWidth: "600px" }}>
            Book your luxury suite or plan your dream royal wedding on our lush green peacock lawns.
          </p>
          <Link to="/booking" className="btn btn-warning rounded-pill px-5 py-3 fw-bold text-dark shadow-lg" style={{ background: "linear-gradient(135deg, #FEA116, #D4AF37)", letterSpacing: "0.05em" }}>
            BOOK YOUR STAY &amp; EVENT NOW
          </Link>
        </div>
      </section>

      {/* 3D COVERFLOW LIGHTBOX MODAL - PORTALED DIRECTLY TO DOCUMENT.BODY FOR 100% FULLSCREEN BLUR */}
      {lightboxIndex !== null && filteredItems[lightboxIndex] && ReactDOM.createPortal(
        (() => {
          const curr = lightboxIndex;
          const total = filteredItems.length;
          const prevIdx = curr === 0 ? total - 1 : curr - 1;
          const nextIdx = curr === total - 1 ? 0 : curr + 1;

          const currentItem = filteredItems[curr];
          const prevItem = filteredItems[prevIdx];
          const nextItem = filteredItems[nextIdx];

          const goPrev = (e) => {
            e.stopPropagation();
            setLightboxIndex(prevIdx);
          };

          const goNext = (e) => {
            e.stopPropagation();
            setLightboxIndex(nextIdx);
          };

          return (
            <div 
              className="hk-lightbox-3d-backdrop position-fixed d-flex align-items-center justify-content-center overflow-hidden" 
              style={{ 
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(11, 29, 55, 0.94)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                zIndex: 99999999,
                userSelect: "none"
              }}
              onClick={() => setLightboxIndex(null)}
            >
              {/* Main 3D Stage Container */}
              <div className="d-flex align-items-center justify-content-center position-relative w-100 h-100" style={{ maxWidth: "1200px" }}>
                
                {/* LEFT PREVIEW CARD */}
                <div 
                  className="hk-3d-side-card hk-3d-left-card d-none d-lg-block position-absolute"
                  onClick={goPrev}
                  style={{
                    left: "4%",
                    width: "340px",
                    height: "340px",
                    borderRadius: "24px",
                    overflow: "hidden",
                    opacity: 0.45,
                    transform: "scale(0.85) perspective(1000px) rotateY(15deg)",
                    filter: "brightness(0.5)",
                    border: "2px solid rgba(212, 175, 55, 0.4)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                    cursor: "pointer",
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    zIndex: 2,
                  }}
                >
                  <img src={prevItem.src} alt={prevItem.title} className="w-100 h-100" style={{ objectFit: "cover" }} />
                </div>

                {/* CENTER ACTIVE CARD CONTAINER */}
                <div 
                  className="hk-3d-active-card-box position-relative"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* CLOSE BUTTON - Attached to Top-Right Corner of Center Card */}
                  <button
                    type="button"
                    className="hk-3d-close-btn shadow-lg"
                    onClick={() => setLightboxIndex(null)}
                    aria-label="Close modal"
                  >
                    <i className="fas fa-times" />
                  </button>

                  {/* LEFT NAVIGATION ARROW - Attached to Left Edge of Center Card */}
                  <button
                    type="button"
                    className="hk-3d-nav-btn hk-3d-prev-btn shadow-lg"
                    onClick={goPrev}
                    aria-label="Previous image"
                  >
                    <i className="fas fa-chevron-left" />
                  </button>

                  {/* MAIN IMAGE DISPLAY */}
                  <div className="hk-3d-img-container">
                    <img
                      src={currentItem.src}
                      alt={currentItem.title}
                      className="w-100 h-100"
                      style={{ objectFit: "cover", objectPosition: "center" }}
                    />
                  </div>

                  {/* RIGHT NAVIGATION ARROW - Attached to Right Edge of Center Card */}
                  <button
                    type="button"
                    className="hk-3d-nav-btn hk-3d-next-btn shadow-lg"
                    onClick={goNext}
                    aria-label="Next image"
                  >
                    <i className="fas fa-chevron-right" />
                  </button>

                  {/* BOTTOM TITLE & COUNTER PILL BADGE */}
                  <div className="text-center mt-3">
                    <div className="hk-3d-badge px-3 px-md-4 py-2 rounded-pill shadow-sm">
                      <span className="hk-3d-badge-title">
                        {currentItem.title}
                      </span>
                      <span className="hk-3d-badge-counter ms-2">
                        ({curr + 1} OF {total})
                      </span>
                    </div>
                  </div>
                </div>

                {/* RIGHT PREVIEW CARD */}
                <div 
                  className="hk-3d-side-card hk-3d-right-card d-none d-lg-block position-absolute"
                  onClick={goNext}
                  style={{
                    right: "4%",
                    width: "340px",
                    height: "340px",
                    borderRadius: "24px",
                    overflow: "hidden",
                    opacity: 0.45,
                    transform: "scale(0.85) perspective(1000px) rotateY(-15deg)",
                    filter: "brightness(0.5)",
                    border: "2px solid rgba(212, 175, 55, 0.4)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                    cursor: "pointer",
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    zIndex: 2,
                  }}
                >
                  <img src={nextItem.src} alt={nextItem.title} className="w-100 h-100" style={{ objectFit: "cover" }} />
                </div>

              </div>
            </div>
          );
        })(),
        document.body
      )}

      <style>{`
        /* GALLERY CARDS - SMOOTH ROUNDED CORNERS */
        .hk-gallery-card {
          border-radius: 20px !important;
          overflow: hidden !important;
          cursor: pointer;
          transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.35s ease, border-color 0.35s ease;
          border: 1.5px solid rgba(212, 175, 55, 0.3) !important;
          background: #FFFFFF;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .hk-gallery-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 36px rgba(254, 161, 22, 0.22) !important;
          border-color: #FEA116 !important;
        }

        .hk-gallery-img-wrapper {
          position: relative;
          height: 220px;
          overflow: hidden;
          background: #0B1D37;
          border-top-left-radius: 20px;
          border-top-right-radius: 20px;
        }

        .hk-gallery-img-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: transform 0.5s ease;
        }

        .hk-gallery-card:hover .hk-gallery-img-wrapper img {
          transform: scale(1.08);
        }

        /* HOVER ZOOM OVERLAY & CENTERED GOLD BUTTON */
        .hk-gallery-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(11, 29, 55, 0.2) 0%, rgba(11, 29, 55, 0.75) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 2;
          cursor: pointer;
        }

        .hk-gallery-card:hover .hk-gallery-overlay {
          opacity: 1 !important;
        }

        .hk-gallery-zoom-btn {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%);
          color: #050E1A;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.15rem;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
          transform: scale(0.8);
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease;
          cursor: pointer;
        }

        .hk-gallery-card:hover .hk-gallery-zoom-btn {
          transform: scale(1.08);
          box-shadow: 0 8px 24px rgba(254, 161, 22, 0.5);
        }

        .hk-gallery-card-body {
          padding: 16px;
          background: #FFFFFF;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom-left-radius: 20px;
          border-bottom-right-radius: 20px;
        }

        .hk-gallery-card-title {
          margin: 0;
          font-family: 'Cinzel', serif;
          font-weight: 700;
          font-size: 0.92rem;
          color: #0B1D37;
        }

        .hk-gallery-card-cat {
          font-family: 'Poppins', sans-serif;
          font-size: 0.76rem;
          color: #53647C;
        }

        .hk-wall-hanging-card {
          position: relative;
          padding-top: 25px;
          transition: transform 0.4s ease;
        }

        .hk-wall-hanging-card:hover {
          transform: translateY(-6px) rotate(0deg) scale(1.03);
        }

        .hk-frame-0 { transform: rotate(-1.5deg); }
        .hk-frame-1 { transform: rotate(1.8deg); }
        .hk-frame-2 { transform: rotate(-2.2deg); }
        .hk-frame-3 { transform: rotate(1.4deg); }

        .hk-pin-nail {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: radial-gradient(circle at 35% 35%, #FFF, #C59B27 60%, #856209);
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          z-index: 5;
          box-shadow: 0 3px 6px rgba(0,0,0,0.5);
        }

        .hk-pin-string-svg {
          width: 100%;
          height: 28px;
          position: absolute;
          top: 4px;
          left: 0;
          z-index: 4;
        }

        .hk-polaroid-frame {
          background: #FFFFFF;
          padding: 10px 10px 14px 10px;
          border-radius: 16px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
          border: 1px solid rgba(212,175,55,0.3);
        }

        .hk-polaroid-img-box {
          height: 190px;
          border-radius: 12px;
          overflow: hidden;
        }

        .hk-polaroid-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .hk-polaroid-title {
          font-family: 'Cinzel', serif;
          font-size: 0.8rem;
          font-weight: 700;
          color: #0B1D37;
        }

        /* 3D COVERFLOW LIGHTBOX STYLES & 100% MOBILE RESPONSIVENESS */
        .hk-3d-active-card-box {
          width: 90vw;
          max-width: 660px;
          background: #FFFFFF;
          border-radius: 28px;
          padding: 16px 16px 20px 16px;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7), 0 0 0 3px rgba(212, 175, 55, 0.8);
          z-index: 10;
          position: relative;
        }

        .hk-3d-img-container {
          width: 100%;
          height: 55vh;
          max-height: 440px;
          border-radius: 20px;
          overflow: hidden;
          background: #0B1D37;
        }

        .hk-3d-close-btn {
          position: absolute;
          top: -14px;
          right: -14px;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: #0B1D37;
          border: 2px solid #FEA116;
          color: #FFFFFF;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 25;
          box-shadow: 0 6px 18px rgba(0,0,0,0.4);
          transition: transform 0.25s ease, background 0.25s ease;
        }

        .hk-3d-close-btn:hover {
          transform: scale(1.12) rotate(90deg);
          background: #FEA116;
          color: #050E1A;
        }

        .hk-3d-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 46px;
          height: 46px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%);
          border: 2.5px solid #FFFFFF;
          color: #050E1A;
          font-size: 1.15rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 25;
          box-shadow: 0 8px 24px rgba(254, 161, 22, 0.5);
          transition: transform 0.25s ease;
        }

        .hk-3d-prev-btn { left: -22px; }
        .hk-3d-next-btn { right: -22px; }

        .hk-3d-nav-btn:hover {
          transform: translateY(-50%) scale(1.12);
        }

        .hk-3d-badge {
          display: inline-flex;
          align-items: center;
          background: #FAF6F0;
          border: 1.5px solid rgba(212, 175, 55, 0.5);
        }

        .hk-3d-badge-title {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #0B1D37;
          font-size: 0.92rem;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .hk-3d-badge-counter {
          color: #FEA116;
          font-weight: 700;
          font-family: 'Poppins', sans-serif;
          font-size: 0.85rem;
        }

        /* MOBILE VIEW OPTIMIZATIONS (< 768px) */
        @media (max-width: 768px) {
          .hk-3d-active-card-box {
            width: 92vw !important;
            max-width: 100% !important;
            padding: 10px 10px 14px 10px !important;
            border-radius: 20px !important;
          }

          .hk-3d-img-container {
            height: 48vh !important;
            max-height: 380px !important;
            border-radius: 14px !important;
          }

          .hk-3d-close-btn {
            top: -12px !important;
            right: -10px !important;
            width: 34px !important;
            height: 34px !important;
            font-size: 0.95rem !important;
          }

          .hk-3d-nav-btn {
            width: 40px !important;
            height: 40px !important;
            font-size: 1rem !important;
          }

          .hk-3d-prev-btn { left: -14px !important; }
          .hk-3d-next-btn { right: -14px !important; }

          .hk-3d-badge-title {
            font-size: 0.78rem !important;
          }
          .hk-3d-badge-counter {
            font-size: 0.75rem !important;
          }
        }
      `}</style>
    </>
  );
}
